import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, merge, Subscription } from 'rxjs';
import { AuthPageLayoutComponent } from 'src/app/components/auth-page-layout/auth-page-layout.component';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { PATTERN } from 'src/app/shared/constants';
import { clearServerErrors } from 'src/app/shared/utils';

@Component({
  selector: 'app-login',
  imports: [RouterModule, ReactiveFormsModule, TranslateModule, IconModule, MaterialModule, AuthPageLayoutComponent],
  templateUrl: './login.component.html'
})
export class LoginComponent implements OnInit, OnDestroy {
  readonly passwordMaxLength = 16;
  loginForm: FormGroup;
  unsubscribe: Subscription[] = [];
  spinLoader = false;
  showPassword = false;

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private toastService: ToastService,
    private authService: AuthService) { }

  get showServerError() {
    return this.hasError('password', 'serverError');
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm() {
    this.loginForm = this.formBuilder.group({
      email: [null, [Validators.required, Validators.pattern(PATTERN.EMAIL)]],
      password: [null, [Validators.required, Validators.maxLength(this.passwordMaxLength)]]
    });

    this.unsubscribe.push(
      merge(this.loginForm.get('email')!.valueChanges, this.loginForm.get('password')!.valueChanges).subscribe(() => clearServerErrors(this.loginForm))
    );
  }

  isInvalid(key: string) {
    const control = this.loginForm.get(key);
    return !!control && control.invalid && control.touched;
  }

  hasError(key: string, errorType: string) {
    return !!this.loginForm.get(key)?.hasError(errorType);
  }

  onSubmit() {
    clearServerErrors(this.loginForm);

    if (!this.loginForm.valid) return this.loginForm.markAllAsTouched();
    this.spinLoader = true;
    const { email, password } = this.loginForm.value;
    this.authService.login({ email, password })
      .pipe(finalize(() => (this.spinLoader = false)))
      .subscribe({
        next: ({ accessToken, refreshToken, encryptedCompanyId }: any) => {
          this.authService.setToken(accessToken, refreshToken, encryptedCompanyId);
          this.router.navigate(['/']);
        },
        error: ({ error, status }) => {
          if (status === 401 && typeof error === 'string') {
            this.loginForm.get('email')?.setErrors({ serverError: error });
            this.loginForm.get('password')?.setErrors({ serverError: error });
          }
          else if(status === 429 && error?.Message) {
            this.toastService.warning(this.translateService.instant(error?.Message), undefined, { timeOut: 5000 });
          }
          else {
            this.toastService.error(this.translateService.instant(error.Message));
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
